const boundElements = new WeakSet();

export function initializeScheduleFields(root = document) {
    const elements = [];

    if (root.matches?.('.schedule-builder')) {
        elements.push(root);
    }

    root.querySelectorAll?.('.schedule-builder').forEach(element => elements.push(element));

    elements.forEach(setup);
}

function setup(el) {
    if (boundElements.has(el)) {
        return;
    }

    boundElements.add(el);

    el.addEventListener('click', event => {
        const frequency = event.target.closest('[data-mode]');

        if (frequency) {
            setActiveMode(el, frequency.dataset.mode);
            refresh(el);

            return;
        }

        const toggle = event.target.closest('[data-dow], [data-dom]');

        if (toggle && !isLastSelected(el, toggle)) {
            toggle.setAttribute('aria-pressed', toggle.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
            refresh(el);
        }
    });

    el.addEventListener('change', event => {
        if (event.target.closest('[data-schedule]')) {
            refresh(el);
        }
    });

    refresh(el, true);
}

function isLastSelected(el, toggle) {
    if (toggle.getAttribute('aria-pressed') !== 'true') {
        return false;
    }

    const group = 'dow' in toggle.dataset ? '[data-dow]' : '[data-dom]';

    return pressedValues(el, group).length === 1;
}

function setActiveMode(el, mode) {
    el.querySelectorAll('[data-mode]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.mode === mode));
    });

    el.querySelectorAll('.schedule-panel').forEach(panel => {
        panel.classList.toggle('is-active', panel.dataset.panel === mode);
    });
}

function activeMode(el) {
    return el.querySelector('[data-mode][aria-pressed="true"]')?.dataset.mode ?? 'hourly';
}

function pressedValues(el, selector) {
    return [...el.querySelectorAll(`${selector}[aria-pressed="true"]`)].map(button =>
        Number(button.dataset.dow ?? button.dataset.dom));
}

function selectValue(el, key) {
    const host = el.querySelector(`[data-schedule="${key}"]`);

    if (!host) {
        return '';
    }

    const inner = host.querySelector('select');

    if (inner) {
        return inner.value;
    }

    const option = host.querySelector('option[selected]') ?? host.querySelector('option');

    return option ? option.value : '';
}

function to24(hour12, meridiem) {
    hour12 = Number(hour12);

    if (meridiem === 'AM') {
        return hour12 === 12 ? 0 : hour12;
    }

    return hour12 === 12 ? 12 : hour12 + 12;
}

function readTime(el, prefix) {
    return {
        h: to24(selectValue(el, `${prefix}-h`), selectValue(el, `${prefix}-ap`)),
        m: Number(selectValue(el, `${prefix}-m`)),
    };
}

function readState(el) {
    return {
        mode:         activeMode(el),
        minInterval:  Number(selectValue(el, 'minInterval')),
        hourInterval: Number(selectValue(el, 'hourInterval')),
        hourlyMinute: Number(selectValue(el, 'hourlyMinute')),
        daily:        readTime(el, 'daily'),
        weekly:       { ...readTime(el, 'weekly'), days: pressedValues(el, '[data-dow]') },
        monthly:      { ...readTime(el, 'monthly'), days: pressedValues(el, '[data-dom]') },
    };
}

function buildCron(state) {
    switch (state.mode) {
        case 'minutes':
            return `*/${state.minInterval} * * * *`;
        case 'hourly':
            return `${state.hourlyMinute} */${state.hourInterval} * * *`;
        case 'daily':
            return `${state.daily.m} ${state.daily.h} * * *`;
        case 'weekly':
            return `${state.weekly.m} ${state.weekly.h} * * ${sortNumbers(state.weekly.days).join(',')}`;
        case 'monthly':
            return `${state.monthly.m} ${state.monthly.h} ${sortNumbers(state.monthly.days).join(',')} * *`;
        default:
            return null;
    }
}

function sortNumbers(values) {
    return [...values].sort((a, b) => a - b);
}

function refresh(el, initial = false) {
    writeValue(el, el.querySelector('.js-schedule-value'), buildCron(readState(el)), initial);
}

function writeValue(el, hidden, value, initial) {
    if (!hidden || hidden.value === value) {
        return;
    }

    hidden.value = value;

    if (!initial) {
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
    }

    el.dispatchEvent(new CustomEvent('schedule:changed', { bubbles: true }));
}
